<template>
  <v-card
    :elevation="previewMode ? 0 : 1"
    :flat="previewMode"
    class="pa-4 d-flex flex-column pgm-dashboard-container rounded-lg"
    :class="{ rounded: !previewMode }"
    style="gap: 8px; width: 100%"
    data-testid="aggregate-learning-leaderboard-preview"
    :ripple="false"
  >
    <div
      class="d-flex"
      :class="titleAlignment === 'center' ? 'flex-column align-center' : 'align-end'"
      style="gap: 8px"
    >
      <span :style="titleStyle" :class="titleClass" style="flex: 1">
        {{ resolvedTitleText }}
      </span>
      <Typography
        variant="caption-2"
        class="mediumGrey--text"
        :style="
          titleAlignment === 'right' ? 'order: -1; white-space: nowrap' : 'white-space: nowrap'
        "
      >
        {{ displayTotalParticipants }} participants
      </Typography>
    </div>

    <div class="d-flex justify-center">
      <AggregateLearningLeaderboardSort
        :value="sortValue"
        :accent-color="resolvedAccentColor"
        :disabled="loading"
        @input="onSortChanged"
      />
    </div>

    <div
      v-if="loading"
      class="d-flex justify-center py-8"
      data-testid="aggregate-learning-leaderboard-minimized-loading"
    >
      <v-progress-circular indeterminate :color="resolvedAccentColor" />
    </div>
    <div v-else class="d-flex flex-column" style="gap: 8px">
      <AggregateLearningLeaderboardRow
        v-for="(row, index) in displayRows"
        :key="`${row.rank}-${row.userId || index}`"
        :rank="row.rank"
        :display-name="row.displayName"
        :initials="row.initials"
        :avatar-url="row.avatarUrl"
        :learning-completed="row.learningCompleted"
        :badges-earned="row.badgesEarned"
        :is-current-user="row.isCurrentUser"
        :active-sort="sortValue"
        :accent-color="resolvedAccentColor"
      />
    </div>

    <div class="d-flex justify-center">
      <Button
        :color="resolvedAccentColor"
        :disabled="previewMode || loading"
        data-testid="view-full-leaderboard-btn"
        :small="isMobile"
        @click="onViewFullClick"
      >
        View Full Leaderboard
      </Button>
    </div>

    <AggregateLearningLeaderboardFullView
      v-if="!previewMode"
      v-model="showFullLeaderboard"
      :accent-color="resolvedAccentColor"
      :title-config="resolvedTitleConfig"
    />
  </v-card>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import AggregateLearningLeaderboardRow from "@/components/aggregate-learning-leaderboard/AggregateLearningLeaderboardRow.vue";
import AggregateLearningLeaderboardSort from "@/components/aggregate-learning-leaderboard/AggregateLearningLeaderboardSort.vue";
import AggregateLearningLeaderboardFullView from "@/components/aggregate-learning-leaderboard/AggregateLearningLeaderboardFullView.vue";
import activeClientMixin from "@/mixins/activeClientMixin";
import isMobileMixin from "@/mixins/isMobile";
import AggregateLeaderboardService from "@/services/AggregateLeaderboardService";

export default {
  name: "AggregateLearningLeaderboardMinimizedView",
  mixins: [activeClientMixin, isMobileMixin],
  components: {
    Typography,
    Button,
    AggregateLearningLeaderboardRow,
    AggregateLearningLeaderboardSort,
    AggregateLearningLeaderboardFullView,
  },
  props: {
    config: {
      type: Object,
      default: () => ({}),
    },
    totalParticipants: {
      type: Number,
      default: 0,
    },
    rows: {
      type: Array,
      default: () => [],
    },
    // When true, the component renders from the `rows` / `totalParticipants` props (used by the
    // dashboard widget creator's live previewMode, which feeds mock data). When false, the component
    // self-fetches the widget data for the active client.
    previewMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sortValue: "learning",
      clientPrimaryColor: null,
      fetchedRows: [],
      fetchedTotalParticipants: 0,
      leaderboardService: new AggregateLeaderboardService(),
      showFullLeaderboard: false,
      loading: false,
      mockRows: [
        {
          rank: 1,
          displayName: "Alex M.",
          initials: "AM",
          avatarUrl: null,
          learningCompleted: 12,
          badgesEarned: 3,
          isCurrentUser: false,
        },
        {
          rank: 2,
          displayName: "Jordan S.",
          initials: "JS",
          avatarUrl: null,
          learningCompleted: 9,
          badgesEarned: 2,
          isCurrentUser: true,
        },
        {
          rank: 3,
          displayName: "Taylor R.",
          initials: "TR",
          avatarUrl: null,
          learningCompleted: 8,
          badgesEarned: 2,
          isCurrentUser: false,
        },
        {
          rank: 4,
          displayName: "Casey P.",
          initials: "CP",
          avatarUrl: null,
          learningCompleted: 7,
          badgesEarned: 1,
          isCurrentUser: false,
        },
        {
          rank: 5,
          displayName: "Morgan L.",
          initials: "ML",
          avatarUrl: null,
          learningCompleted: 6,
          badgesEarned: 1,
          isCurrentUser: false,
        },
      ],
    };
  },
  computed: {
    themeAccentColor() {
      return this.$vuetify?.theme?.themes?.light?.brandPrimary;
    },
    resolvedAccentColor() {
      return this.clientPrimaryColor || this.themeAccentColor;
    },
    resolvedTitleConfig() {
      return this.config?.title || {};
    },
    resolvedTitleText() {
      return this.resolvedTitleConfig?.text || "Leaderboard";
    },
    titleStyle() {
      return {
        color: this.resolvedTitleConfig?.color || "#000000",
        textDecoration: this.resolvedTitleConfig?.underline ? "underline" : undefined,
        minWidth: 0,
      };
    },
    titleAlignment() {
      return (
        this.resolvedTitleConfig?.horizontalAlignment || this.resolvedTitleConfig?.align || "left"
      );
    },
    titleClass() {
      const size = this.resolvedTitleConfig?.size || "lg";
      const classes = [`pgm-dashboard-text-${size}`, `text-${this.titleAlignment}`];
      if (this.resolvedTitleConfig?.bold) classes.push("font-weight-bold");
      if (this.resolvedTitleConfig?.italic) classes.push("font-italic");
      return classes;
    },

    displayRows() {
      if (this.previewMode) {
        return this.rows.length > 0 ? this.rows : this.mockRows;
      }
      return this.fetchedRows;
    },
    displayTotalParticipants() {
      if (this.previewMode) {
        return this.totalParticipants > 0 ? this.totalParticipants : this.mockRows.length;
      }
      return this.fetchedTotalParticipants;
    },
    leaderboardClientId() {
      return this.userProfile?.clientId ?? null;
    },
  },
  async mounted() {
    await this.loadClientPrimaryColor();
    if (!this.previewMode) {
      this.fetchLeaderboard();
    }
  },
  watch: {
    leaderboardClientId() {
      this.loadClientPrimaryColor();
      if (!this.previewMode) {
        this.fetchLeaderboard();
      }
    },
    sortValue() {
      if (!this.previewMode) {
        this.fetchLeaderboard();
      }
    },
  },
  methods: {
    onSortChanged(newSortValue) {
      this.sortValue = newSortValue;
    },
    onViewFullClick() {
      if (this.previewMode) return;
      this.showFullLeaderboard = true;
    },
    async loadClientPrimaryColor() {
      this.clientPrimaryColor = null;
      try {
        const value = await this.getClientField("primaryColor");
        if (value) this.clientPrimaryColor = value;
      } catch (e) {
        console.log("Failed to resolve client primaryColor for leaderboard", e);
      }
    },
    async fetchLeaderboard() {
      if (this.previewMode) return;
      if (this.leaderboardClientId == null) return;
      this.loading = true;
      try {
        const response = await this.leaderboardService.getLeaderboardWidget(
          this.leaderboardClientId,
          {
            sort: this.sortValue,
          }
        );
        this.fetchedRows = Array.isArray(response?.rows) ? response.rows : [];
        this.fetchedTotalParticipants = response?.totalUsers ?? 0;
      } catch (e) {
        console.log("Failed to fetch aggregate learning leaderboard widget", e);
        this.fetchedRows = [];
        this.fetchedTotalParticipants = 0;
        this.$store?.dispatch("showError", {
          message: "Failed to load leaderboard. Please try again later.",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
